import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';

import { usePostHog } from 'posthog-js/react'

interface RetentionRates {
  first: number;
  second: number;
  third: number;
  subsequent: number;
}

interface CustomersCalculatorProps {
  onCalculate: () => void;
  isAuthenticated: boolean;
  retentionRates: RetentionRates;
  setRetentionRates: React.Dispatch<React.SetStateAction<RetentionRates>>;
  price: number;
  setPrice: React.Dispatch<React.SetStateAction<number>>;
  currentCustomerArpu: number;
  setCurrentCustomerArpu: React.Dispatch<React.SetStateAction<number>>;
}

interface Results {
  sixMonth: number;
  twelveMonth: number;
  twentyFourMonth: number;
  lifetime: number;
  addRevenue: {
    sixMonth: number;
    twelveMonth: number;
    twentyFourMonth: number;
    lifetime: number;
  };
  oldRevenue: {
    sixMonth: number;
    twelveMonth: number;
    twentyFourMonth: number;
    lifetime: number;
  };
  currentRevenue: {
    sixMonth: number;
    twelveMonth: number;
    twentyFourMonth: number;
    lifetime: number;
  };
}

const CustomersCalculator: React.FC<CustomersCalculatorProps> = ({ 
  onCalculate, 
  isAuthenticated, 
  retentionRates, 
  setRetentionRates,
  price,
  setPrice,
  currentCustomerArpu,
  setCurrentCustomerArpu
}) => {

  const posthog = usePostHog()
  const [initialCustomers, setInitialCustomers] = useState<number>(() => {
    const saved = localStorage.getItem('customers_initial');
    return saved ? Number(saved) : 1000;
  });

  const [newCustomersPerPeriod, setNewCustomersPerPeriod] = useState<number>(() => {
    const saved = localStorage.getItem('customers_new_per_period');
    return saved ? Number(saved) : 100;
  });

  const [frequency, setFrequency] = useState<'monthly' | 'quarterly' | 'annual'>(() => {
    const saved = localStorage.getItem('customers_frequency');
    return (saved as 'monthly' | 'quarterly' | 'annual') || 'monthly';
  });

  const [currentChurnRate, setCurrentChurnRate] = useState<number>(() => {
    const saved = localStorage.getItem('current_churn_rate');
    return saved ? Number(saved) : 10;
  });

  const [results, setResults] = useState<Results | null>(null);

  useEffect(() => {
    localStorage.setItem('customers_initial', initialCustomers.toString());
  }, [initialCustomers]);

  useEffect(() => {
    localStorage.setItem('customers_new_per_period', newCustomersPerPeriod.toString());
  }, [newCustomersPerPeriod]);

  useEffect(() => {
    localStorage.setItem('customers_frequency', frequency);
  }, [frequency]);

  useEffect(() => {
    localStorage.setItem('current_customer_arpu', currentCustomerArpu.toString());
  }, [currentCustomerArpu]);

  const handleCurrentChurnRateChange = (value: number) => {
    const newChurnRate = value;
    setCurrentChurnRate(newChurnRate);
    localStorage.setItem('current_churn_rate', newChurnRate.toString());
  };

  const getPaymentsPerYear = (freq: 'monthly' | 'quarterly' | 'annual'): number => {
    switch (freq) {
      case 'monthly':
        return 12;
      case 'quarterly':
        return 4;
      case 'annual':
        return 1;
      default:
        return 12;
    }
  };

  const calculateCustomerRetention = (
    months: number,
    initial: number,
    newPerPeriod: number,
    freq: 'monthly' | 'quarterly' | 'annual',
    rates: RetentionRates
  ): number => {
    const paymentsPerYear = getPaymentsPerYear(freq);
    const paymentInterval = 12 / paymentsPerYear;
    const numberOfPayments = Math.ceil(months / paymentInterval);
    

    let cohorts: number[] = []; // Track each cohort of new customers

    // Add initial cohort of new customers for each period
    for (let i = 0; i < numberOfPayments; i++) {
      cohorts.push(newPerPeriod);
    }

    // Calculate retention for each cohort
    for (let period = 0; period < numberOfPayments; period++) {
      for (let cohortIndex = 0; cohortIndex <= period; cohortIndex++) {
        const periodsOld = period - cohortIndex;
        let retentionRate;
        let baseCustomers = newPerPeriod;

        if (periodsOld === 0) {
          retentionRate = rates.first / 100;
        } else if (periodsOld === 1) {
          retentionRate = rates.second / 100;
        } else if (periodsOld === 2) {
          retentionRate = rates.third / 100;
        } else if (periodsOld > 2) {
          baseCustomers = newPerPeriod * (rates.third / 100);
          retentionRate = 1;
          // Apply subsequent churn for each period after the third
          for (let i = 0; i < periodsOld - 2; i++) {
            retentionRate *= (100 - rates.subsequent) / 100;
          }
        }

        cohorts[cohortIndex] = baseCustomers * (retentionRate || 0);
      }
    }

    // Sum up all remaining customers from all cohorts
    const retainedNewCustomers = cohorts.reduce((sum, cohort) => sum + cohort, 0);
    
    const coeff = Math.pow(((100 - currentChurnRate) / 100), months)
    
    const totalCustomers = Math.round(initial * coeff + retainedNewCustomers);
    
    return totalCustomers
  };

  const calculateResults = () => {
    if (initialCustomers === null || newCustomersPerPeriod === null) return;

    const sixMonth = calculateCustomerRetention(6, initialCustomers, newCustomersPerPeriod, frequency, retentionRates);
    const sixMonthNew = calculateCustomerRetention(6, 0, newCustomersPerPeriod, frequency, retentionRates);
    const twelveMonth = calculateCustomerRetention(12, initialCustomers, newCustomersPerPeriod, frequency, retentionRates);
    const twelveMonthNew = calculateCustomerRetention(12, 0, newCustomersPerPeriod, frequency, retentionRates);
    const twentyFourMonth = calculateCustomerRetention(24, initialCustomers, newCustomersPerPeriod, frequency, retentionRates);
    const twentyFourMonthNew = calculateCustomerRetention(24, 0, newCustomersPerPeriod, frequency, retentionRates);
    const lifetime = calculateCustomerRetention(120, initialCustomers, newCustomersPerPeriod, frequency, retentionRates);
    const lifetimeNew = calculateCustomerRetention(120, 0, newCustomersPerPeriod, frequency, retentionRates);

    const paymentsPerYear = getPaymentsPerYear(frequency);
    const monthlyNewCustomerPrice = price / (12 / paymentsPerYear);
    const monthlyCurrentCustomerPrice = currentCustomerArpu / (12 / paymentsPerYear);

    const numberOfCustomers = {
      sixMonth,
      twelveMonth,
      twentyFourMonth,
      lifetime
    };

    const numberOfCustomersNew = {
      sixMonth: sixMonthNew,
      twelveMonth: twelveMonthNew,
      twentyFourMonth: twentyFourMonthNew,
      lifetime: lifetimeNew
    };

    const addRevenue = {
      sixMonth: 0,
      twelveMonth: 0,
      twentyFourMonth: 0,
      lifetime: 0
    };

    const oldRevenue = {
      sixMonth: 0,
      twelveMonth: 0,
      twentyFourMonth: 0,
      lifetime: 0
    };

    const currentRevenue = {
      sixMonth: 0,
      twelveMonth: 0,
      twentyFourMonth: 0,
      lifetime: 0
    };

    const periodDictionary: Record<number, keyof RetentionRates> = {
      1: 'first',
      2: 'second',
      3: 'third',
    } as const;

    [
      { period: 6, key: 'sixMonth' },
      { period: 12, key: 'twelveMonth' },
      { period: 24, key: 'twentyFourMonth' },
      { period: 120, key: 'lifetime' }
    ].forEach(({ period, key }) => {
      
      let totalPeriodNewRevenue = 0;
      let endOfPeriodNewRevenue = 0;
      let lastMonthCustomers = 0;

      // For each month in the period
      for (let startMonth = 0; startMonth < period; startMonth++) {
          // Calculate how many customers remain from this cohort
          let remainingCustomers = newCustomersPerPeriod;
          
          // Only calculate revenue for the remaining months in the period
          for (let monthsAfterStart = 0; monthsAfterStart < period - startMonth; monthsAfterStart++) {
              // Add revenue for this month from this cohort
              totalPeriodNewRevenue += remainingCustomers * monthlyNewCustomerPrice;
              
              // Calculate retention for next month
              if (monthsAfterStart < 2) { // We already counted this month, so check next month
                  remainingCustomers *= retentionRates[periodDictionary[monthsAfterStart + 1]] / 100;
              } else {
                  remainingCustomers *= (100 - retentionRates.subsequent) / 100;
              }

              // If we're at the last month of the period, these customers contribute to final monthly revenue
              if (monthsAfterStart === period - startMonth - 1) {
                  lastMonthCustomers += remainingCustomers;
                  endOfPeriodNewRevenue += lastMonthCustomers * monthlyNewCustomerPrice;
              }


          }
      }

      addRevenue[key as keyof typeof addRevenue] = Math.round(totalPeriodNewRevenue);
      
      // Calculate revenue from initial customers for each month in the period
      let totalOldRevenue = 0;
      let remainingInitialCustomers = initialCustomers;

      // Calculate revenue for each month in the period
      for (let month = 0; month < period; month++) {
          // Add revenue for this month
          totalOldRevenue += remainingInitialCustomers * monthlyCurrentCustomerPrice;
          
          // Apply churn rate for next month
          remainingInitialCustomers *= (100 - currentChurnRate) / 100;
      }

      oldRevenue[key as keyof typeof oldRevenue] = Math.round(totalOldRevenue);

      // Calculate end of period monthly revenue from both old and new customers
      let endOfPeriodOldRevenue = initialCustomers * monthlyCurrentCustomerPrice * Math.pow((100 - currentChurnRate) / 100, period);
      
      endOfPeriodNewRevenue = monthlyNewCustomerPrice * numberOfCustomersNew[key as keyof typeof numberOfCustomers] 


      // Current revenue is the monthly revenue at the end of the period
      currentRevenue[key as keyof typeof currentRevenue] = Math.round(
          monthlyNewCustomerPrice * numberOfCustomersNew[key as keyof typeof numberOfCustomers] + // Revenue from all remaining new customers
          endOfPeriodOldRevenue // Revenue from remaining old customers
      );
    });

    setResults({
      sixMonth,
      twelveMonth,
      twentyFourMonth,
      lifetime,
      addRevenue,
      oldRevenue,
      currentRevenue
    });

    onCalculate();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    calculateResults();
    posthog.capture('submit_customers_calculator', { 
      frequency: frequency,
      initial_customers: initialCustomers,
      new_customers_per_period: newCustomersPerPeriod,
      current_customer_arpu: currentCustomerArpu,
      current_churn_rate: currentChurnRate,
      retention_rates: retentionRates
    });
  }

  const handleRetentionChange = (field: keyof RetentionRates, value: string) => {
    const numValue = Math.min(100, Math.max(0, Number(value)));
    setRetentionRates(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  return (
    <div className="calculator">
      <h2>Future Revenue Calculator</h2>
      <p style={{marginBottom: '2rem', marginTop: '1rem'}}>Calculate how much money you're going to make in 6/12/24/120 months if you maintain current retention and acquisition rates. Plan how much you need to grow to hit your objectives. </p>
      
      <form className="calculator-form" onSubmit={handleSubmit}>
  
        <div className="input-group horizontal">
          <div>
            <label>Current Customer Base</label>
            <input
              type="number"
              value={initialCustomers}
              onChange={(e) => setInitialCustomers(Number(e.target.value))}
              min="0"
            />
          </div>

          <div>
            <label>Current Revenue per User</label>
            <input
              type="number"
              value={currentCustomerArpu}
              onChange={(e) => setCurrentCustomerArpu(Number(e.target.value))}
              min="0"
            />
          </div>
          <div>
            <label>Current Churn Rate, %</label>
            <input
                type="number"
                value={currentChurnRate}
                onChange={(e) => handleCurrentChurnRateChange(Number(e.target.value))}
                min="0"
              />
            </div>
        </div>
      
        <div className="input-group horizontal">
          <div>
          <label>New Customers Added Per Period</label>
          <input
            type="number"
            value={newCustomersPerPeriod}
            onChange={(e) => setNewCustomersPerPeriod(Number(e.target.value))}
            min="0"
          />
        </div>
        <div>
          <label>New Customer Revenue per User</label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            min="0"
          />
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="payment-frequency">Period Length</label>
          <select
            id="payment-frequency"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as 'monthly' | 'quarterly' | 'annual')}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </div>

        <div className="input-group">
          <label htmlFor="first-payment">First Period Retention Rate (% New Customers Paying First Bill)</label>
          <div className="slider-input-group">
            <input
              type="range"
              id="first-payment-slider"
              value={retentionRates.first || 0}
              onChange={(e) => handleRetentionChange('first', e.target.value)}
              min="0"
              max="100"
              step="1"
              className="slider"
            />
            <input
              type="number"
              id="first-payment"
              value={retentionRates.first || ''}
              onChange={(e) => handleRetentionChange('first', e.target.value)}
              min="0"
              max="100"
              step="0.1"
              placeholder="95"
              required
              className="number-input"
            />
            <span>%</span>
          </div>
          <small className="helper-text">
            Example: If you add {newCustomersPerPeriod} new customers and retention is {retentionRates.first}%, then {Math.round(newCustomersPerPeriod * retentionRates.first / 100)} will remain after the first period
          </small>
        </div>

        <div className="input-group">
          <label htmlFor="second-payment">Second Period Retention Rate (% New Customers Paying Second Bill)</label>
          <div className="slider-input-group">
            <input
              type="range"
              id="second-payment-slider"
              value={retentionRates.second || 0}
              onChange={(e) => handleRetentionChange('second', e.target.value)}
              min="0"
              max="100"
              step="1"
              className="slider"
            />
            <input
              type="number"
              id="second-payment"
              value={retentionRates.second || ''}
              onChange={(e) => handleRetentionChange('second', e.target.value)}
              min="0"
              max="100"
              step="0.1"
              placeholder="90"
              required
              className="number-input"
            />
            <span>%</span>
          </div>
          <small className="helper-text">
            Example: From {newCustomersPerPeriod} new customers, {Math.round(newCustomersPerPeriod * retentionRates.second / 100)} will remain after the second period
          </small>
        </div>

        <div className="input-group">
          <label htmlFor="third-payment">Third Period Retention Rate (% New Customers Paying Third Bill)</label>
          <div className="slider-input-group">
            <input
              type="range"
              id="third-payment-slider"
              value={retentionRates.third || 0}
              onChange={(e) => handleRetentionChange('third', e.target.value)}
              min="0"
              max="100"
              step="1"
              className="slider"
            />
            <input
              type="number"
              id="third-payment"
              value={retentionRates.third || ''}
              onChange={(e) => handleRetentionChange('third', e.target.value)}
              min="0"
              max="100"
              step="0.1"
              placeholder="85"
              required
              className="number-input"
            />
            <span>%</span>
          </div>
          <small className="helper-text">
            Example: From {newCustomersPerPeriod} new customers, {Math.round(newCustomersPerPeriod * retentionRates.third / 100)} will remain after the third period
          </small>
        </div>

        <div className="input-group">
          <label htmlFor="subsequent-payment">Subsequent Period Churn Rate</label>
          <div className="slider-input-group">
            <input
              type="range"
              id="subsequent-payment-slider"
              value={retentionRates.subsequent || 0}
              onChange={(e) => handleRetentionChange('subsequent', e.target.value)}
              min="0"
              max="100"
              step="1"
              className="slider"
            />
            <input
              type="number"
              id="subsequent-payment"
              value={retentionRates.subsequent || ''}
              onChange={(e) => handleRetentionChange('subsequent', e.target.value)}
              min="0"
              max="100"
              step="0.1"
              placeholder="10"
              required
              className="number-input"
            />
            <span>%</span>
          </div>
          <small className="helper-text">
            Example: If you have {Math.round(newCustomersPerPeriod * retentionRates.third / 100)} customers after the third period and the churn is {retentionRates.subsequent}%, you'll keep {Math.round(newCustomersPerPeriod * retentionRates.third / 100 * (100 - retentionRates.subsequent) / 100)} customers for subsequent periods
          </small>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button type="submit" className="calculate-button">
            Calculate Customer Base
          </button>
          {results && (
            <button
              type="button"
              className="icon-button"
              onClick={() => {
                const resultsText = `
                SaaSSyTools - Revenue Calculator Results

                Settings:
                Initial Customers: ${initialCustomers.toLocaleString()}
                New Customers per ${frequency}: ${newCustomersPerPeriod.toLocaleString()}
                New Customer Price: €${price.toLocaleString()} per ${frequency}
                Current Customer ARPU: €${currentCustomerArpu.toLocaleString()} per ${frequency}
                Current Customer Churn: ${currentChurnRate}%

                Retention Rates:
                First ${frequency}: ${retentionRates.first}%
                Second ${frequency}: ${retentionRates.second}%
                Third ${frequency}: ${retentionRates.third}%
                Subsequent ${frequency}s: ${retentionRates.subsequent}% churn

                Customer Base:
                6-Month: ${results.sixMonth.toLocaleString()} customers
                12-Month: ${results.twelveMonth.toLocaleString()} customers
                24-Month: ${results.twentyFourMonth.toLocaleString()} customers
                Long-term: ${results.lifetime.toLocaleString()} customers

                Revenue from New Customers:
                6-Month: €${results.addRevenue.sixMonth.toLocaleString()}
                12-Month: €${results.addRevenue.twelveMonth.toLocaleString()}
                24-Month: €${results.addRevenue.twentyFourMonth.toLocaleString()}
                Long-term: €${results.addRevenue.lifetime.toLocaleString()}

                Revenue from Old Customers:
                6-Month: €${results.oldRevenue.sixMonth.toLocaleString()}
                12-Month: €${results.oldRevenue.twelveMonth.toLocaleString()}
                24-Month: €${results.oldRevenue.twentyFourMonth.toLocaleString()}
                Long-term: €${results.oldRevenue.lifetime.toLocaleString()}

                Revenue per Cycle:
                After 6 Months: €${results.currentRevenue.sixMonth.toLocaleString()}
                After 12 Months: €${results.currentRevenue.twelveMonth.toLocaleString()}
                After 24 Months: €${results.currentRevenue.twentyFourMonth.toLocaleString()}
                Long-term: €${results.currentRevenue.lifetime.toLocaleString()}
                `;
                navigator.clipboard.writeText(resultsText).then(() => {
                  toast.success('Results copied to clipboard!', {
                    duration: 2000,
                    position: 'bottom-right',
                  });
                });

                posthog.capture('copy_customers_data', { 
                  frequency: frequency,
                  initial_customers: initialCustomers,
                  new_customers_per_period: newCustomersPerPeriod,
                  current_customer_arpu: currentCustomerArpu,
                  current_churn_rate: currentChurnRate,
                  retention_rates: retentionRates
                });
              }}
              title="Copy results"
            >
              <ClipboardDocumentIcon className="icon" />
            </button>
          )}
        </div>
      </form>

      <Toaster />

      {results && (
        <>
        <div id="results">
          <div className="result-item">
            <h3>6-Month Customer Base</h3>
            <p className="result-value">
              {results.sixMonth.toLocaleString()} customers
            </p>
          </div>
          <div className="result-item">
            <h3>12-Month Customer Base</h3>
            <p className="result-value">
              {results.twelveMonth.toLocaleString()} customers
            </p>
       
          </div>
          <div className="result-item">
            <h3>24-Month Customer Base</h3>
            <p className="result-value">
              {results.twentyFourMonth.toLocaleString()} customers
            </p>
      
          </div>
          <div className="result-item">
            <h3>Long-term Customer Base</h3>
            <p className="result-value">
              {results.lifetime.toLocaleString()} customers
            </p>
       
          </div>
        </div>
        <div id="result-old-customers-revenue">
          <div className="result-item">
            <h3>6-Month Revenue from Old Customers</h3>
            <p className="result-value">
              €{results.oldRevenue.sixMonth.toLocaleString()} 
            </p>
          
          </div>
          <div className="result-item">
          <h3>12-Month Revenue from Old Customers</h3>
   
            <p className="result-value">
              €{results.oldRevenue.twelveMonth.toLocaleString()} 
            </p>
           
          </div>
          <div className="result-item">
          <h3>24-Month Revenue from Old Customers</h3>
          
            <p className="result-value">
              €{results.oldRevenue.twentyFourMonth.toLocaleString()} 
            </p>
          
          </div>
          <div className="result-item">
          <h3>Long-Term Revenue from Old Customers</h3>
          
            <p className="result-value">
              €{results.oldRevenue.lifetime.toLocaleString()}
            </p>
          
          </div>
        </div>
        <div id="result-new-customers-revenue">
          <div className="result-item">
            <h3>6-Month Revenue from New Customers</h3>
            <p className="result-value">
              €{results.addRevenue.sixMonth.toLocaleString()} 
            </p>
          
          </div>
          <div className="result-item">
          <h3>12-Month Revenue from New Customers</h3>
   
            <p className="result-value">
              €{results.addRevenue.twelveMonth.toLocaleString()} 
            </p>
           
          </div>
          <div className="result-item">
          <h3>24-Month Revenue from New Customers</h3>
          
            <p className="result-value">
              €{results.addRevenue.twentyFourMonth.toLocaleString()} 
            </p>
          
          </div>
          <div className="result-item">
          <h3>Long-Term Revenue from New Customers</h3>
          
            <p className="result-value">
              €{results.addRevenue.lifetime.toLocaleString()}
            </p>
          
          </div>
        </div>

        <div id="results-monthly">
          <div className="result-item">
            <h3>Revenue / Cycle after 6 Months</h3>
            <p className="result-value">
              €{results.currentRevenue.sixMonth.toLocaleString()} 
            </p>
          </div>
          <div className="result-item">
          <h3>Revenue / Cycle after 12 Months</h3>
       
            <p className="result-value">
              €{results.currentRevenue.twelveMonth.toLocaleString()} 
            </p>
      
          </div>
          <div className="result-item">
          <h3>Revenue / Cycle after 24 Months</h3>
    
            <p className="result-value">
              €{results.currentRevenue.twentyFourMonth.toLocaleString()}
            </p>
        
          </div>
          <div className="result-item">
          <h3>Revenue / Cycle Long-Term</h3>
         
            <p className="result-value">
              €{results.currentRevenue.lifetime.toLocaleString()} 
            </p>
        
          </div>
        </div>
        
        </>
      )}
    </div>
  );
};

export default CustomersCalculator;
